import { apiClient } from "../../../api/client";
import type {
  AMAssignedClient,
  CasePortfolioFilters,
  CasePortfolioPage,
  CaseSeverityStats,
  CaseDueThisWeekStats,
  AMAssessmentReportFilters,
  AMAssessmentReportPage,
  AMAssessmentPortfolioStats,
  AMCustomerHealthReport,
  AMCustomerHealthReportParams,
  AMComplianceAlertReport,
  AMComplianceAlertReportParams,
  AMAssessmentThroughputReport,
  AMAssessmentThroughputParams,
  AMSummary,
} from "../types";

export const accountManagerApi = {
  summary: {
    /** GET /case-management/account-manager/me/summary */
    get: async (): Promise<AMSummary> => {
      const response = await apiClient.get<AMSummary>(
        "/case-management/account-manager/me/summary",
      );
      return response.data;
    },
  },

  customers: {
    /** GET /user-management/tenants/account-managers/me/assigned-tenants */
    list: async (): Promise<AMAssignedClient[]> => {
      const response = await apiClient.get<AMAssignedClient[]>(
        "/user-management/tenants/account-managers/me/assigned-tenants",
      );
      return response.data;
    },
  },

  portfolioCases: {
    /** GET /case-management/account-manager/cases */
    list: async (
      filters?: CasePortfolioFilters,
    ): Promise<CasePortfolioPage> => {
      const response = await apiClient.get<CasePortfolioPage>(
        "/case-management/account-manager/cases",
        { params: filters as Record<string, unknown> },
      );
      return response.data;
    },

    /** GET /case-management/account-manager/cases/stats/by-severity */
    statsBySeverity: async (): Promise<CaseSeverityStats> => {
      const response = await apiClient.get<CaseSeverityStats>(
        "/case-management/account-manager/cases/stats/by-severity",
      );
      return response.data;
    },

    /** GET /case-management/account-manager/cases/stats/due-this-week */
    statsDueThisWeek: async (params?: {
      referenceDate?: string;
      timezone?: string;
    }): Promise<CaseDueThisWeekStats> => {
      const response = await apiClient.get<CaseDueThisWeekStats>(
        "/case-management/account-manager/cases/stats/due-this-week",
        params ? { params: params as Record<string, unknown> } : {},
      );
      return response.data;
    },
  },

  portfolioReports: {
    /** GET /assessment-service/account-manager/needs-assessment */
    list: async (
      filters?: AMAssessmentReportFilters,
    ): Promise<AMAssessmentReportPage> => {
      const response = await apiClient.get<AMAssessmentReportPage>(
        "/assessment-service/account-manager/needs-assessment",
        { params: filters as Record<string, unknown> },
      );
      return response.data;
    },

    /** GET /assessment-service/account-manager/needs-assessment/stats */
    stats: async (params?: {
      status?: number;
    }): Promise<AMAssessmentPortfolioStats> => {
      const response = await apiClient.get<AMAssessmentPortfolioStats>(
        "/assessment-service/account-manager/needs-assessment/stats",
        params ? { params: params as Record<string, unknown> } : {},
      );
      return response.data;
    },
  },

  customerHealthReport: {
    /** GET /assessment-service/account-manager/reports/tenants?fromDate=&toDate= */
    get: async (
      params: AMCustomerHealthReportParams,
    ): Promise<AMCustomerHealthReport> => {
      const response = await apiClient.get<AMCustomerHealthReport>(
        "/assessment-service/account-manager/reports/tenants",
        { params: params as unknown as Record<string, unknown> },
      );
      return response.data;
    },
  },

  complianceAlertReport: {
    /** GET /assessment-service/account-manager/reports/cases?fromDate=&toDate=&severity= */
    get: async (
      params: AMComplianceAlertReportParams,
    ): Promise<AMComplianceAlertReport> => {
      const response = await apiClient.get<AMComplianceAlertReport>(
        "/assessment-service/account-manager/reports/cases",
        { params: params as unknown as Record<string, unknown> },
      );
      return response.data;
    },
  },

  assessmentThroughputReport: {
    /** GET /assessment-service/account-manager/reports/needs-assessments?fromDate=&toDate=&type= */
    get: async (
      params: AMAssessmentThroughputParams,
    ): Promise<AMAssessmentThroughputReport> => {
      const response = await apiClient.get<AMAssessmentThroughputReport>(
        "/assessment-service/account-manager/reports/needs-assessments",
        { params: params as unknown as Record<string, unknown> },
      );
      return response.data;
    },
  },
};
